import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { ArrowLeft, ArrowRight, Copy, Download, Eye, FileSpreadsheet, KeyRound, MoreHorizontal, PencilLine, Plus, RotateCcw, Search, Shapes, ShieldCheck, ShieldOff, Upload } from 'lucide-react'
import { Button } from '../../../../components/ui/button'
import { Badge } from '../../../../components/ui/badge'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../../../components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select'
import { Input } from '../../../../components/ui/input'
import { Alert, AlertDescription } from '../../../../components/ui/alert'
import { TableLoadingState } from '../../../../components/ui/table-loading-state'
import { changeManagedStudentMascot, resetStudentPin } from '../../../../api/backend/student-credentials'
import { useAuth } from '../../../../contexts/AuthContext'
import { STUDENT_MASCOTS, STUDENT_SECRET_EMOJIS } from '../../../../lib/robolabx-login-catalog'
import { resolveStudentDisplayPassword, resolveStudentMascotDisplay, resolveStudentNameDisplay, resolveStudentUsernameDisplay } from '../../../../lib/student-display'
import {
  appendStudentSecretId,
  buildAddStudentFullName,
  buildCreateStudentPayloadFromWizard,
  buildStudentUsernameForMascotChange,
  canContinueAddStudentBasics,
  canSubmitAddStudent,
  clearStudentSecretIds,
  generateRandomStudentSecretIds,
  parseStudentUsername,
  renderStudentSecretEmojis,
} from '../../../../lib/student-creation'
import { ADMIN_PAGE_CONTENT_CLASSNAME } from '../layoutStyles'
import {
  createStudentsBulk,
  createStudentsBulkFromCsv,
  fetchStudents,
  updateMember,
  toggleMemberStatus,
  createStudent,
  downloadStudentBulkCsvTemplate,
} from '../../../../api/backend'
import type { StudentItem, AgeTier, CreateStudentPayload, BulkCreateStudentsResponse } from '../../../../api/backend'

const PAGE_SIZE = 10

const AGE_TIER_OPTIONS: { value: string; label: string }[] = [
  { value: 'UNDER_13', label: 'Under 13' },
  { value: 'TEEN', label: 'Teen' },
]

const AGE_TIER_LABELS: Record<AgeTier, string> = {
  UNDER_13: 'Under 13',
  TEEN: 'Teen',
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  student: StudentItem
  onClose: () => void
  onSave: (updated: StudentItem) => void
}

function EditModal({ student, onClose, onSave }: EditModalProps) {
  const { getAccessToken } = useAuth()
  const [firstName, setFirstName] = useState(student.givenName ?? '')
  const [lastName, setLastName] = useState(student.surName ?? '')
  const [ageTier, setAgeTier] = useState<string>(student.ageTier ?? '')
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    const token = getAccessToken()
    if (!token) return
    setIsSaving(true)
    try {
      const payload: Parameters<typeof updateMember>[2] = {}
      payload.givenName = firstName.trim()
      payload.surName = lastName.trim()
      payload.ageTier = (ageTier as AgeTier) || null
      const updated = await updateMember(token, student.id, payload)
      onSave(updated)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save changes')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Edit Student</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                First Name
              </label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                Last Name
              </label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="grid gap-1.5">
            <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
              Age Tier
            </label>
            <Select value={ageTier} onValueChange={setAgeTier}>
              <SelectTrigger>
                <SelectValue placeholder="— Select age tier —" />
              </SelectTrigger>
              <SelectContent>
                {AGE_TIER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </div>

        <DialogFooter>
          <Button variant="neutral" size="sm" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Add Student Modal ────────────────────────────────────────────────────────

interface AddStudentModalProps {
  onClose: () => void
  onCreated: (student: StudentItem) => void
}

function AddStudentModal({ onClose, onCreated }: AddStudentModalProps) {
  const { getAccessToken } = useAuth()
  const [step, setStep] = useState<'profile' | 'mascot' | 'secret'>('profile')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [ageTier, setAgeTier] = useState<string>('')
  const [selectedMascotId, setSelectedMascotId] = useState<string | null>(null)
  const [selectedSecretIds, setSelectedSecretIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const fullName = useMemo(() => buildAddStudentFullName(firstName, lastName), [firstName, lastName])
  const selectedMascot = useMemo(
    () => STUDENT_MASCOTS.find((item) => item.id === selectedMascotId) ?? null,
    [selectedMascotId],
  )
  const canContinueProfile = canContinueAddStudentBasics({
    givenName: firstName,
    surName: lastName,
    ageTier: ageTier as AgeTier | '',
  })
  const canSubmit = canSubmitAddStudent({
    givenName: firstName,
    surName: lastName,
    ageTier: ageTier as AgeTier | '',
    mascotId: selectedMascotId,
    secretIds: selectedSecretIds,
  })

  const handleBack = () => {
    if (isSaving) return
    setStep((current) => (current === 'secret' ? 'mascot' : 'profile'))
  }

  const handleSecretTap = (secretId: string) => {
    if (isSaving) return
    setSelectedSecretIds((current) => appendStudentSecretId(current, secretId))
  }

  const handleUndoSecretTap = () => {
    if (isSaving || selectedSecretIds.length === 0) return
    setSelectedSecretIds(clearStudentSecretIds())
  }

  const handleCreate = async () => {
    const token = getAccessToken()
    if (!token) return
    if (!canSubmit) {
      toast.error('Finish all 3 steps before creating the student.')
      return
    }
    setIsSaving(true)
    try {
      const payload: CreateStudentPayload = buildCreateStudentPayloadFromWizard({
        givenName: firstName,
        surName: lastName,
        ageTier: ageTier as AgeTier,
        mascotId: selectedMascotId,
        secretIds: selectedSecretIds,
      })
      const created = await createStudent(token, payload)
      onCreated(created)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create student')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[calc(100vh-2rem)] max-w-[720px] overflow-hidden" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Add Student</DialogTitle>
        </DialogHeader>

        <div
          className="max-h-[calc(100vh-12rem)] overflow-x-visible overflow-y-auto px-1 py-1"
        >
          <div className="flex flex-col gap-6">
            {step === 'profile' ? (
              <div className="grid gap-4 pt-1">
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                      First Name
                    </label>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="grid gap-1.5">
                    <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                      Last Name
                    </label>
                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>

                <div className="grid gap-1.5">
                  <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                    Age Tier
                  </label>
                  <Select value={ageTier} onValueChange={setAgeTier}>
                    <SelectTrigger>
                      <SelectValue placeholder="— Select age tier —" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_TIER_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : null}

            {step === 'mascot' ? (
              <div className="grid gap-4 pt-1">
                <div>
                  <div className="text-2xl font-heading text-foreground">{fullName || 'New student'}</div>
                  <div className="mt-2 text-sm text-foreground/65">Pick the mascot this student will use at sign in.</div>
                </div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {STUDENT_MASCOTS.map((mascot) => {
                    const isSelected = mascot.id === selectedMascotId
                    return (
                      <button
                        key={mascot.id}
                        type="button"
                        onClick={() => setSelectedMascotId(mascot.id)}
                        className={[
                          'aspect-square max-h-[110px] max-w-[110px] rounded-base border-2 p-3 text-left transition-colors',
                          isSelected
                            ? 'border-nb-border bg-main text-main-foreground nb-shadow'
                            : 'border-nb-border bg-secondary-bg text-foreground nb-shadow hover:bg-main/10',
                        ].join(' ')}
                      >
                        <div className="grid h-full grid-rows-[1fr_auto]">
                          <div className="flex items-start text-[1.75rem] leading-none">{mascot.emoji}</div>
                          <div className="font-heading text-[0.92rem] leading-none">{mascot.label}</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : null}

            {step === 'secret' ? (
              <div className="grid gap-4 pt-1">
                <div className="rounded-base border-2 border-nb-border bg-secondary-bg p-3 nb-shadow">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{selectedMascot?.emoji ?? '•'}</div>
                    <div>
                      <div className="font-heading text-xl leading-none">{fullName || 'New student'}</div>
                      <div className="mt-1 text-sm text-foreground/60">{selectedMascot?.label ?? 'No mascot selected'}</div>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-foreground/65">Tap 3 emoji in order. This will be the student's starter sign-in secret.</div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {STUDENT_SECRET_EMOJIS.map((secret) => (
                    <button
                      key={secret.id}
                      type="button"
                      onClick={() => handleSecretTap(secret.id)}
                      disabled={isSaving || selectedSecretIds.length >= 3}
                      className="aspect-square max-h-[110px] max-w-[110px] rounded-base border-2 border-nb-border bg-secondary-bg p-2.5 text-left nb-shadow transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none disabled:opacity-50"
                    >
                      <div className="grid h-full grid-rows-[1fr_auto]">
                        <div className="flex items-start text-[1.6rem] leading-none">{secret.emoji}</div>
                        <div className="font-heading text-[0.85rem] leading-none break-words">{secret.label}</div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex gap-2">
                    {Array.from({ length: 3 }).map((_, index) => {
                      const secret = STUDENT_SECRET_EMOJIS.find((item) => item.id === selectedSecretIds[index])
                      return (
                        <div
                          key={index}
                          className="flex h-12 w-12 items-center justify-center rounded-base border-2 border-nb-border bg-secondary-bg text-2xl"
                        >
                          {secret?.emoji ?? <span className="text-lg text-foreground/30">•</span>}
                        </div>
                      )
                    })}
                  </div>
                  <Button
                    type="button"
                    variant="neutral"
                    size="icon"
                    aria-label="Undo last tap"
                    onClick={handleUndoSecretTap}
                    disabled={selectedSecretIds.length === 0 || isSaving}
                    className="shrink-0"
                  >
                    <RotateCcw className="size-4" />
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="neutral" size="sm" onClick={step === 'profile' ? onClose : handleBack} disabled={isSaving}>
            {step === 'profile' ? 'Cancel' : (
              <>
                <ArrowLeft className="size-4" />
                Back
              </>
            )}
          </Button>
          {step === 'profile' ? (
            <Button size="sm" onClick={() => setStep('mascot')} disabled={!canContinueProfile || isSaving}>
              Next
            </Button>
          ) : null}
          {step === 'mascot' ? (
            <Button size="sm" onClick={() => setStep('secret')} disabled={!selectedMascotId || isSaving}>
              Next
              <ArrowRight className="size-4" />
            </Button>
          ) : null}
          {step === 'secret' ? (
            <Button size="sm" onClick={handleCreate} disabled={!canSubmit || isSaving}>
              {isSaving ? 'Creating…' : 'Add Student'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ViewLoginModalProps {
  student: StudentItem
  onClose: () => void
}

function ViewLoginModal({ student, onClose }: ViewLoginModalProps) {
  const mascot = resolveStudentMascotDisplay(student)
  const displayPassword = resolveStudentDisplayPassword(student)
  const usernameDisplay = resolveStudentUsernameDisplay(student)

  const handleCopy = async () => {
    const lines = [
      `Student: ${usernameDisplay}`,
      `Mascot: ${mascot.emoji ? `${mascot.emoji} ` : ''}${mascot.label === '—' ? 'Unavailable' : mascot.label}`,
      `Picture secret: ${displayPassword}`,
    ]
    await navigator.clipboard.writeText(lines.join('\n'))
    toast.success('Login details copied')
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Student Login Details</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4">
          <p className="text-sm text-foreground/60">
            Use these details to help {usernameDisplay} sign in.
          </p>

          <div className="rounded-base border-2 border-nb-border bg-secondary-bg p-4">
            <div className={`grid gap-4 ${displayPassword ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
              <div>
                <div className="text-xs font-heading uppercase tracking-[0.14em] text-foreground/45">Student</div>
                <div className="mt-2 text-lg font-heading text-foreground">{usernameDisplay}</div>
              </div>
              <div>
                <div className="text-xs font-heading uppercase tracking-[0.14em] text-foreground/45">Mascot</div>
                <div className="mt-2 text-lg font-heading text-foreground">
                  {mascot.emoji ? `${mascot.emoji} ` : ''}{mascot.label === '—' ? 'Unavailable' : mascot.label}
                </div>
              </div>
              <div>
                <div className="text-xs font-heading uppercase tracking-[0.14em] text-foreground/45">Picture secret</div>
                <div className="mt-2 text-lg font-heading text-foreground">{displayPassword}</div>
              </div>
            </div>
          </div>

          <div className="rounded-base border-2 border-nb-border bg-main/10 p-4 text-sm text-foreground/75">
            Students sign in with full name, mascot, and picture secret.
          </div>
        </div>

        <DialogFooter>
          <Button variant="neutral" size="sm" onClick={() => void handleCopy()}>
            <Eye className="size-4" />
            Copy details
          </Button>
          <Button size="sm" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ChangeMascotModalProps {
  student: StudentItem
  onClose: () => void
  onSaved: (changes: { username: string; mascotId: string; mascotLabel: string; mascotEmoji: string }) => void
}

function ChangeMascotModal({ student, onClose, onSaved }: ChangeMascotModalProps) {
  const { getAccessToken } = useAuth()
  const parsedUsername = parseStudentUsername(student.username)
  const [selectedMascotId, setSelectedMascotId] = useState<string | null>(student.mascotId ?? parsedUsername.mascotId)
  const [isSaving, setIsSaving] = useState(false)

  const currentUsername = student.username?.trim() || student.name

  const handleSave = async () => {
    if (!selectedMascotId) {
      toast.error('Select a mascot before saving.')
      return
    }

    const selectedMascot = STUDENT_MASCOTS.find((item) => item.id === selectedMascotId)
    if (!selectedMascot) {
      toast.error('Selected mascot is invalid.')
      return
    }

    setIsSaving(true)
    try {
      const token = getAccessToken()
      if (!token) return
      const username = buildStudentUsernameForMascotChange(selectedMascot.id, currentUsername)
      await changeManagedStudentMascot(token, {
        memberId: student.id,
        username,
      })
      onSaved({
        username,
        mascotId: selectedMascot.id,
        mascotLabel: selectedMascot.label,
        mascotEmoji: selectedMascot.emoji,
      })
      toast.success('Mascot updated')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update mascot')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[calc(100vh-2rem)] max-w-[720px] overflow-hidden" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Change Mascot</DialogTitle>
        </DialogHeader>

        <div className="max-h-[calc(100vh-12rem)] overflow-y-auto px-1 pb-2 pt-1">
          <div className="grid gap-4">
            <div>
              <div className="text-2xl font-heading text-foreground">{student.name}</div>
              <div className="mt-2 text-sm text-foreground/65">Choose a new mascot. The login username will be updated to match.</div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {STUDENT_MASCOTS.map((mascot) => {
                const isSelected = mascot.id === selectedMascotId
                return (
                  <button
                    key={mascot.id}
                    type="button"
                    onClick={() => setSelectedMascotId(mascot.id)}
                    className={[
                      'aspect-square max-h-[110px] max-w-[110px] rounded-base border-2 p-3 text-left transition-all',
                      isSelected
                        ? 'border-nb-border bg-main text-main-foreground nb-shadow'
                        : 'border-nb-border bg-secondary-bg text-foreground hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none nb-shadow',
                    ].join(' ')}
                  >
                    <div className="grid h-full grid-rows-[1fr_auto]">
                      <div className="flex items-start text-[1.75rem] leading-none">{mascot.emoji}</div>
                      <div className="font-heading text-[0.92rem] leading-none">{mascot.label}</div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="neutral" size="sm" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={!selectedMascotId || isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ResetPinSuccessModalProps {
  studentName: string
  secretIds: string[]
  onClose: () => void
}

function ResetPinSuccessModal({ studentName, secretIds, onClose }: ResetPinSuccessModalProps) {
  const pictureSecretDisplay = renderStudentSecretEmojis(secretIds)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(`Picture secret: ${pictureSecretDisplay}`)
    toast.success('Picture secret copied')
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Picture Secret Reset</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4">
          <p className="text-sm text-foreground/70">
            {studentName}&apos;s picture secret has been reset.
          </p>

          <div className="rounded-base border-2 border-nb-border bg-secondary-bg p-4">
            <div className="text-xs font-heading uppercase tracking-[0.14em] text-foreground/45">Picture secret</div>
            <div className="mt-3 text-3xl">{pictureSecretDisplay}</div>
          </div>

          <p className="text-sm text-foreground/60">
            Reset PIN restores the default picture secret for this account.
          </p>
        </div>

        <DialogFooter>
          <Button variant="neutral" size="sm" onClick={() => void handleCopy()}>
            <Copy className="size-4" />
            Copy
          </Button>
          <Button size="sm" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type BulkUploadMode = 'paste' | 'csv'

interface BulkUploadModalProps {
  onClose: () => void
  onCompleted: () => void
}

function BulkUploadModal({ onClose, onCompleted }: BulkUploadModalProps) {
  const { getAccessToken } = useAuth()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [mode, setMode] = useState<BulkUploadMode>('paste')
  const [pasteText, setPasteText] = useState('Mia Chen,2018-05-12\nNoah Patel,2015-09-23')
  const [selectedCsvFile, setSelectedCsvFile] = useState<File | null>(null)
  const [selectedCsvName, setSelectedCsvName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleTemplateDownload = async () => {
    const token = getAccessToken()
    if (!token) return
    try {
      const blob = await downloadStudentBulkCsvTemplate(token)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'student-bulk-upload-template.csv'
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download CSV template')
    }
  }

  const handleCsvChange = (file?: File | null) => {
    if (!file) return
    setSelectedCsvName(file.name)
    setSelectedCsvFile(file)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const reportBulkResult = (result: BulkCreateStudentsResponse) => {
    if (result.failedCount > 0) {
      toast.warning(`Created ${result.createdCount} students, ${result.failedCount} failed.`)
    } else {
      toast.success(`Created ${result.createdCount} student${result.createdCount === 1 ? '' : 's'}.`)
    }
  }

  const parsePastedStudents = () => {
    return pasteText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [fullNamePart, birthdayPart] = line.split(',').map((part) => part.trim())
        const nameParts = (fullNamePart ?? '').split(/\s+/).filter(Boolean)
        const givenName = nameParts[0] ?? ''
        const surName = nameParts.slice(1).join(' ')
        return {
          givenName,
          surName,
          dateOfBirth: birthdayPart || undefined,
        }
      })
  }

  const handleSubmit = async () => {
    const token = getAccessToken()
    if (!token) return

    if (mode === 'paste') {
      const students = parsePastedStudents()
      if (students.length === 0) {
        toast.error('Paste at least one student row.')
        return
      }
      if (students.some((student) => !student.givenName || !student.dateOfBirth)) {
        toast.error('Each row must include a first name and birthday.')
        return
      }
      setIsSubmitting(true)
      try {
        const result = await createStudentsBulk(token, { students })
        reportBulkResult(result)
        onCompleted()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create students')
      } finally {
        setIsSubmitting(false)
      }
      return
    }

    if (!selectedCsvFile) {
      toast.error('Upload a CSV file first.')
      return
    }
    setIsSubmitting(true)
    try {
      const result = await createStudentsBulkFromCsv(token, selectedCsvFile)
      reportBulkResult(result)
      onCompleted()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to import CSV')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Bulk Upload Students</DialogTitle>
        </DialogHeader>

        <div className="grid gap-5">
          <div className="grid grid-cols-2 gap-0 overflow-hidden rounded-base border-2 border-nb-border">
            <button
              type="button"
              className={`h-11 text-sm font-heading transition-colors ${mode === 'paste' ? 'bg-main text-main-foreground' : 'bg-background text-foreground'}`}
              onClick={() => setMode('paste')}
            >
              Paste Text
            </button>
            <button
              type="button"
              className={`h-11 text-sm font-heading transition-colors ${mode === 'csv' ? 'bg-main text-main-foreground' : 'bg-background text-foreground'}`}
              onClick={() => setMode('csv')}
            >
              Import CSV
            </button>
          </div>

          {mode === 'paste' ? (
            <div className="grid gap-3">
              <div className="text-sm font-base">One student per line</div>
              <textarea
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                rows={10}
                className="min-h-56 rounded-base border-2 border-nb-border bg-background px-3 py-3 text-sm outline-none focus:border-main"
                placeholder="Mia Chen,2018-05-12&#10;Noah Patel,2015-09-23"
              />
              <p className="text-sm text-foreground/55">
                Format: Name,Birthday. Mascot and picture secret are assigned by the backend.
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-base">Upload CSV file</div>
                <Button variant="neutral" size="sm" onClick={handleTemplateDownload}>
                  <Download className="size-4" />
                  Download Template
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(event) => handleCsvChange(event.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex min-h-48 flex-col items-center justify-center rounded-base border-2 border-dashed border-nb-border bg-background px-6 py-8 text-center transition-colors hover:border-main"
              >
                <FileSpreadsheet className="mb-4 size-10 text-foreground/40" />
                <div className="text-lg font-base text-foreground/75">
                  {selectedCsvName || 'Click to select a .csv file'}
                </div>
                <div className="mt-2 text-sm text-foreground/50">
                  Columns: name, birthday
                </div>
                {selectedCsvFile ? (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-base border-2 border-nb-border bg-main/10 px-3 py-1 text-sm">
                    <FileSpreadsheet className="size-4" />
                    {selectedCsvFile.name}
                  </div>
                ) : null}
              </button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="neutral" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => void handleSubmit()} disabled={isSubmitting}>
            Create Students
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Students Page ────────────────────────────────────────────────────────────

export default function StudentsPage() {
  const { getAccessToken } = useAuth()
  const [students, setStudents] = useState<StudentItem[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [editingStudent, setEditingStudent] = useState<StudentItem | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [resettingId, setResettingId] = useState<string | null>(null)
  const [viewingStudent, setViewingStudent] = useState<StudentItem | null>(null)
  const [changingMascotStudent, setChangingMascotStudent] = useState<StudentItem | null>(null)
  const [resetPinResult, setResetPinResult] = useState<{ studentName: string; secretIds: string[] } | null>(null)

  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [search])

  const load = useCallback((page: number, keyword?: string) => {
    const token = getAccessToken()
    if (!token) return
    setIsLoading(true)
    setError(null)
    fetchStudents(token, page, PAGE_SIZE, keyword)
      .then((res) => {
        setStudents(res.content)
        setTotalElements(res.totalElements)
        setTotalPages(res.totalPages)
        setCurrentPage(page)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load members')
      })
      .finally(() => setIsLoading(false))
  }, [getAccessToken])

  useEffect(() => { load(0, debouncedSearch) }, [load, debouncedSearch])

  const handleEditSave = (updated: StudentItem) => {
    setStudents((prev) => prev.map((student) => (
      student.id === updated.id
        ? { ...student, ...updated }
        : student
    )))
    setEditingStudent(null)
  }

  const handleCreated = () => {
    setShowAddModal(false)
    load(0, debouncedSearch)
  }

  const handleToggle = async (student: StudentItem) => {
    const token = getAccessToken()
    if (!token) return
    setTogglingId(student.id)
    try {
      await toggleMemberStatus(token, student.id, !student.isActive)
      setStudents((prev) =>
        prev.map((s) => (s.id === student.id ? { ...s, isActive: !s.isActive } : s)),
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update member status')
    } finally {
      setTogglingId(null)
    }
  }

  const goToPage = (page: number) => load(page, debouncedSearch)

  const handleMascotSaved = (studentId: string, changes: { username: string; mascotId: string; mascotLabel: string; mascotEmoji: string }) => {
    setStudents((prev) => prev.map((student) => (
      student.id === studentId
        ? {
            ...student,
            username: changes.username,
            mascotId: changes.mascotId,
            mascotLabel: changes.mascotLabel,
            mascotEmoji: changes.mascotEmoji,
          }
        : student
    )))
    setChangingMascotStudent(null)
  }

  const handleResetPin = async (student: StudentItem) => {
    const token = getAccessToken()
    if (!token) return

    const secretIds = generateRandomStudentSecretIds(3)
    setResettingId(student.id)

    try {
      await resetStudentPin(token, {
        memberId: student.id,
        secretIds,
      })
      setStudents((prev) => prev.map((item) => (
        item.id === student.id
          ? {
              ...item,
              displayPassword: renderStudentSecretEmojis(secretIds),
              pictureSecretIds: secretIds,
              pictureSecretEmojis: secretIds.map((secretId) => STUDENT_SECRET_EMOJIS.find((entry) => entry.id === secretId)?.emoji ?? secretId),
            }
          : item
      )))
      setResetPinResult({
        studentName: student.name,
        secretIds,
      })
      toast.success('Picture secret reset')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reset picture secret')
    } finally {
      setResettingId(null)
    }
  }

  const pageNumbers: (number | 'ellipsis')[] = []
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pageNumbers.push(i)
  } else {
    pageNumbers.push(0)
    if (currentPage > 2) pageNumbers.push('ellipsis')
    for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages - 2, currentPage + 1); i++) {
      pageNumbers.push(i)
    }
    if (currentPage < totalPages - 3) pageNumbers.push('ellipsis')
    pageNumbers.push(totalPages - 1)
  }

  return (
    <div className={ADMIN_PAGE_CONTENT_CLASSNAME}>
      <div className="mb-6">
        <p className="text-sm text-foreground/60">
          One place for student records, login setup, and recovery.
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center gap-3">
            <span>{error}</span>
            <button
              className="ml-auto text-sm underline underline-offset-2 hover:no-underline"
              onClick={() => load(currentPage, debouncedSearch)}
            >
              Retry
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search students..."
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="neutral" onClick={() => setShowBulkUploadModal(true)}>
            <Upload className="w-4 h-4" />
            Bulk Upload
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            Add Student
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="nb-shadow rounded-base overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-main text-main-foreground hover:bg-main">
              <TableHead>Student</TableHead>
              <TableHead>Age Tier</TableHead>
              <TableHead>Classes</TableHead>
              <TableHead>Account Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableLoadingState colSpan={5} label="Loading students..." />
            ) : students.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-16 text-center text-sm text-foreground/40">
                  {search ? 'No students match your search.' : 'No members found.'}
                </TableCell>
              </TableRow>
            ) : (
              students.map((s) => (
                <TableRow key={s.id} className="hover:bg-main/10">
                  <TableCell>
                    {(() => {
                      const mascot = resolveStudentMascotDisplay(s)
                      const nameDisplay = resolveStudentNameDisplay(s)
                      const usernameDisplay = resolveStudentUsernameDisplay(s)

                      return (
                        <div className="flex items-center gap-3">
                          {mascot.emoji ? (
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-nb-border bg-secondary-bg text-xl">
                              {mascot.emoji}
                            </div>
                          ) : null}
                          <div className="min-w-0">
                            <div className="truncate font-base text-sm text-foreground">{nameDisplay}</div>
                            <div className="truncate text-xs text-foreground/45">@{usernameDisplay}</div>
                          </div>
                        </div>
                      )
                    })()}
                  </TableCell>

                  <TableCell>
                    {s.ageTier ? (
                      <Badge variant="neutral" className="px-2 py-0.5">
                        {AGE_TIER_LABELS[s.ageTier]}
                      </Badge>
                    ) : (
                      <span className="text-foreground/40 italic">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {s.classTitles?.length || s.classes?.length ? (
                      <div className="flex max-w-[220px] flex-wrap gap-2">
                        {(s.classTitles ?? s.classes?.map((classroom) => classroom.title) ?? []).slice(0, 3).map((classTitle) => (
                          <Badge
                            key={classTitle}
                            variant="neutral"
                            className="px-2 py-0.5"
                          >
                            {classTitle}
                          </Badge>
                        ))}
                        {(s.classTitles ?? s.classes?.map((classroom) => classroom.title) ?? []).length > 3 && (
                          <Badge variant="muted" className="px-2 py-0.5">
                            +{(s.classTitles ?? s.classes?.map((classroom) => classroom.title) ?? []).length - 3}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-foreground/40 italic">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {s.isActive ? (
                      <Badge variant="success" className="gap-1.5 px-2.5 py-0.5">
                        <span className="inline-block size-1.5 rounded-full bg-current/70" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="muted" className="gap-1.5 px-2.5 py-0.5">
                        <span className="inline-block size-1.5 rounded-full bg-current/70" />
                        Inactive
                      </Badge>
                    )}
                  </TableCell>

                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="neutral" size="sm" className="h-8 w-8 p-0" aria-label={`Actions for ${s.name}`}>
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44 bg-background text-foreground">
                        <DropdownMenuItem className="bg-background text-foreground focus:bg-secondary-bg" onClick={() => setEditingStudent(s)}>
                          <PencilLine className="size-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem className="bg-background text-foreground focus:bg-secondary-bg" onClick={() => setViewingStudent(s)}>
                          <Eye className="size-4" />
                          View Login
                        </DropdownMenuItem>
                        <DropdownMenuItem className="bg-background text-foreground focus:bg-secondary-bg" onClick={() => setChangingMascotStudent(s)}>
                          <Shapes className="size-4" />
                          Change Mascot
                        </DropdownMenuItem>
                        <DropdownMenuItem className="bg-background text-foreground focus:bg-secondary-bg" onClick={() => void handleResetPin(s)}>
                          <KeyRound className="size-4" />
                          {resettingId === s.id ? '…' : 'Reset Pin'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="bg-background text-foreground focus:bg-secondary-bg"
                          disabled={togglingId === s.id}
                          onClick={() => handleToggle(s)}
                        >
                          {s.isActive ? <ShieldOff className="size-4" /> : <ShieldCheck className="size-4" />}
                          {togglingId === s.id ? '…' : s.isActive ? 'Disable' : 'Enable'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!isLoading && !error && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-foreground/40">
            Page {currentPage + 1} of {totalPages} &middot; {totalElements} students
          </span>
          <Pagination className="w-auto mx-0">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => { e.preventDefault(); if (currentPage > 0) goToPage(currentPage - 1) }}
                  aria-disabled={currentPage === 0}
                  className={currentPage === 0 ? 'pointer-events-none opacity-40' : ''}
                />
              </PaginationItem>

              {pageNumbers.map((p, idx) =>
                p === 'ellipsis' ? (
                  <PaginationItem key={`ellipsis-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href="#"
                      isActive={p === currentPage}
                      onClick={(e) => { e.preventDefault(); goToPage(p) }}
                    >
                      {p + 1}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => { e.preventDefault(); if (currentPage < totalPages - 1) goToPage(currentPage + 1) }}
                  aria-disabled={currentPage >= totalPages - 1}
                  className={currentPage >= totalPages - 1 ? 'pointer-events-none opacity-40' : ''}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {editingStudent && (
        <EditModal
          student={editingStudent}
          onClose={() => setEditingStudent(null)}
          onSave={handleEditSave}
        />
      )}

      {showAddModal && (
        <AddStudentModal
          onClose={() => setShowAddModal(false)}
          onCreated={handleCreated}
        />
      )}

      {showBulkUploadModal && (
        <BulkUploadModal
          onClose={() => setShowBulkUploadModal(false)}
          onCompleted={() => {
            setShowBulkUploadModal(false)
            load(0, debouncedSearch)
          }}
        />
      )}

      {viewingStudent && (
        <ViewLoginModal
          student={viewingStudent}
          onClose={() => setViewingStudent(null)}
        />
      )}

      {changingMascotStudent && (
        <ChangeMascotModal
          student={changingMascotStudent}
          onClose={() => setChangingMascotStudent(null)}
          onSaved={(changes) => handleMascotSaved(changingMascotStudent.id, changes)}
        />
      )}

      {resetPinResult && (
        <ResetPinSuccessModal
          studentName={resetPinResult.studentName}
          secretIds={resetPinResult.secretIds}
          onClose={() => setResetPinResult(null)}
        />
      )}
    </div>
  )
}
